import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import api from "../../api/sellerApi";

export default function Account() {
  const { user, roles, logout, setUser, switchRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [billing, setBilling] = useState(null);
  const [stats, setStats] = useState(null);
  const [businessForm, setBusinessForm] = useState({
    business_name: "",
    location_description: "",
    product_type: "",
  });
  const [savingBusiness, setSavingBusiness] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/billing"), api.get("/stats")])
      .then(([b, s]) => {
        setBilling(b.data);
        setStats(s.data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (user?.seller) {
      setBusinessForm({
        business_name: user.seller.name || "",
        location_description: user.seller.location_description || "",
        product_type: user.seller.product_type || "",
      });
    }
  }, [user]);

  const saveBusinessProfile = async () => {
    setSavingBusiness(true);
    try {
      const res = await api.patch("/profile", businessForm);
      setUser((prev) => ({
        ...prev,
        seller: {
          ...prev.seller,
          ...res.data,
        },
      }));
      alert("Business details updated successfully!");
    } catch (err) {
      alert(err.message || "Failed to update business details.");
    } finally {
      setSavingBusiness(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 px-5 py-6 transition-colors">
      <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        My Account
      </h1>

      {/* Personal Profile */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 transition-colors">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
          Personal Profile
        </h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Full Name</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {user?.name}
            </span>
          </div>
          <div className="flex justify-between border-t border-gray-50 dark:border-zinc-850 pt-2 mt-2">
            <span className="text-gray-500 dark:text-gray-400">
              Phone Number
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {user?.phone}
            </span>
          </div>
        </div>
      </div>

      {/* Business Profile (Optional) */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 transition-colors">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
          Business Profile{" "}
          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
            (Optional)
          </span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
          Byka works for anyone sending a delivery. If you're running a
          business, add your details here — it unlocks proper receipts and a
          default pickup address.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
              Business / Shop Name
            </label>
            <input
              type="text"
              value={businessForm.business_name}
              onChange={(e) =>
                setBusinessForm((p) => ({
                  ...p,
                  business_name: e.target.value,
                }))
              }
              placeholder="e.g. Tanaka Boutique"
              className="w-full rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-850 text-sm text-gray-900 dark:text-white px-3.5 py-2.5 focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
              Location / Stall
            </label>
            <input
              type="text"
              value={businessForm.location_description}
              onChange={(e) =>
                setBusinessForm((p) => ({
                  ...p,
                  location_description: e.target.value,
                }))
              }
              placeholder="e.g. Gulf Complex, Stall 14"
              className="w-full rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-850 text-sm text-gray-900 dark:text-white px-3.5 py-2.5 focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
              Product Type
            </label>
            <input
              type="text"
              value={businessForm.product_type}
              onChange={(e) =>
                setBusinessForm((p) => ({ ...p, product_type: e.target.value }))
              }
              placeholder="e.g. Clothing, Electronics"
              className="w-full rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-850 text-sm text-gray-900 dark:text-white px-3.5 py-2.5 focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            />
          </div>

          <button
            onClick={saveBusinessProfile}
            disabled={savingBusiness}
            className="w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-3 rounded-xl text-sm shadow-md active:scale-95 transition-transform disabled:opacity-50 mt-2"
          >
            {savingBusiness ? "Saving..." : "Save business details"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 transition-colors">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
          Activity
        </h2>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="bg-gray-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-gray-100 dark:border-zinc-800 transition-colors">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.total_orders || 0}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Total Orders
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-gray-100 dark:border-zinc-800 transition-colors">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {stats?.completed_orders || 0}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Completed
            </p>
          </div>
        </div>
      </div>

      {/* Billing */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 transition-colors">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
          Billing & Payments
        </h2>
        <div className="space-y-2.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">
              Outstanding Commission
            </span>
            <span className="font-semibold text-red-600">
              ${(billing?.outstanding_commission || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between border-t border-gray-100 dark:border-zinc-800 pt-2.5">
            <span className="text-gray-500 dark:text-gray-400">
              Unsettled Cash Orders
            </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              ${(billing?.unsettled_cash || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Appearance Settings */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 shadow-sm transition-colors">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
          Appearance
        </h2>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
            Dark Mode
          </span>
          <button
            onClick={toggleTheme}
            className={`w-12 h-6 flex items-center rounded-full p-1 duration-300 cursor-pointer ${
              theme === "dark"
                ? "bg-emerald-500 justify-end"
                : "bg-gray-300 justify-start"
            }`}
          >
            <div className="bg-white w-4 h-4 rounded-full shadow-md" />
          </button>
        </div>
      </div>

      <Link
        to="/seller/feedback"
        className="block w-full bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-4 text-center text-green-600 dark:text-emerald-400 font-medium mb-4 transition-colors"
      >
        Give Feedback
      </Link>

      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800 p-5 mb-4 text-center shadow-sm transition-colors">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
          Platform Roles
        </h2>
        {roles?.includes("rider") ? (
          <button
            onClick={() => switchRole("rider")}
            className="w-full bg-emerald-500 text-white py-3 rounded-xl font-semibold text-sm shadow-sm active:scale-95 transition-transform"
          >
            Switch to Rider Dashboard →
          </button>
        ) : (
          <button
            onClick={() => alert("Coming soon: Fast-track Rider Registration!")}
            className="w-full bg-gray-50 dark:bg-zinc-850 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-medium text-sm border border-gray-200 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-zinc-800"
          >
            Also register as a Rider
          </button>
        )}
      </div>

      <button
        onClick={logout}
        className="w-full border-2 border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-gray-400 py-3 rounded-xl font-medium text-sm hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
      >
        Logout
      </button>
    </div>
  );
}
